import { NgxFileDropEntry } from 'ngx-file-drop';
import { RenderExporter, renderExporterOf } from './render-export-guide';
import { checkTextures, folderOf, scanFbxTextures } from './render-fbx-textures';
import { RENDER_MODEL_EXTENSIONS, RENDER_TEXTURE_EXTENSIONS, RENDER_UPLOAD_LIMIT_BYTES } from './render.data';

// Local check of what the user dropped or picked, before anything is uploaded: one model file
// (FBX / USDZ / USD) on its own or in a folder with its textures. Archives are opened and a model is
// picked before this (prepareFiles in render-upload.prepare.ts). Mirrors the rules WebUploadService
// applies on the library drop zone (junk files skipped, one model at a time for non-subscribers)
// and adds the prototype's empty / too-large cases. An FBX's textures are checked here too
// (checkModelTextures); for USD the renderer is still the first to know.

export interface RenderUploadFile {
  // Path inside the dropped folder ('textures/albedo.png'), or just the file name.
  path: string;
  file: File;
}

export interface RenderUploadModel {
  name: string;
  fileName: string;
  format: string;
  model: RenderUploadFile;
  textures: RenderUploadFile[];
  others: RenderUploadFile[];
  fileCount: number;
  totalBytes: number;
  // Read out of the FBX by checkModelTextures: the textures it asks for or carries, and the
  // program that wrote it. Absent for USD and for an FBX that could not be read.
  fbx?: { textures: number; exporter?: RenderExporter };
}

// 'archive': anything about a ZIP (render-upload.prepare.ts), its title and text say which.
export type RenderUploadProblemCode = 'nothing' | 'unsupported' | 'multiple-models' | 'empty' | 'too-large' | 'upload-failed' | 'archive';

export interface RenderUploadProblem {
  code: RenderUploadProblemCode;
  title: string;
  text: string;
  // What the refusal's button offers: another file (the default), or a folder — an archive that
  // cannot be opened here is unpacked on the computer and dropped as one.
  fix?: 'file' | 'folder';
}

export type RenderUploadInspection =
  | { ok: true; model: RenderUploadModel }
  | { ok: false; problem: RenderUploadProblem };

const JUNK_NAMES = new Set(['.ds_store', 'thumbs.db', 'desktop.ini']);

export function isJunkPath(path: string): boolean {
  const parts = path.replace(/\\/g, '/').split('/').filter(Boolean);
  const name = (parts[parts.length - 1] || '').toLowerCase();
  return parts.some((part) => part.toLowerCase() === '__macosx') || JUNK_NAMES.has(name) || name.startsWith('._');
}

export function fileExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot).toLowerCase() : '';
}

export function isModelFile(name: string): boolean {
  return RENDER_MODEL_EXTENSIONS.includes(fileExtension(name));
}

export function isTextureFile(name: string): boolean {
  return RENDER_TEXTURE_EXTENSIONS.includes(fileExtension(name));
}

// The DOM's FileSystemFileEntry.file, with the error callback that ngx-file-drop's typing leaves out.
type ReadableFileEntry = { file(success: (file: File) => void, error?: (error: unknown) => void): void };

// ngx-file-drop hands over entries (files and, for a dropped folder, every file inside it);
// the File objects come asynchronously. Whatever else the drop carried — a link or some text that
// an app drags along with the file (a 'string' item, no entry) — the library still wraps as a
// "file" with no name and no File behind it: skipped, the drop goes on with the real files (it
// threw in isJunkPath and lost the whole drop, 2026-10-20).
export function collectDroppedFiles(entries: NgxFileDropEntry[]): Promise<RenderUploadFile[]> {
  const files = entries
    .filter((entry) => {
      const path = entry.relativePath || entry.fileEntry?.name;
      return entry.fileEntry?.isFile && !!path && !isJunkPath(path);
    })
    .map((entry) => new Promise<RenderUploadFile | null>((resolve) => {
      try {
        (entry.fileEntry as unknown as ReadableFileEntry).file(
          (file) => resolve(file instanceof File ? { path: entry.relativePath || file.name, file } : null),
          // An unreadable entry must not hold up the others.
          () => resolve(null),
        );
      } catch {
        resolve(null);
      }
    }));
  return Promise.all(files).then((list) => list.filter((item): item is RenderUploadFile => !!item));
}

// <input type="file"> and <input webkitdirectory>: webkitRelativePath is set for a folder pick.
export function collectInputFiles(files: FileList | File[] | null | undefined): RenderUploadFile[] {
  return Array.from(files || [])
    .map((file) => ({ path: (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name, file }))
    .filter((item) => !isJunkPath(item.path));
}

export function inspectUpload(files: RenderUploadFile[]): RenderUploadInspection {
  if (files.length === 0) {
    return problem('nothing', 'Nothing to upload.', 'Drop an FBX or USDZ model, or a folder that contains one.');
  }

  const models = files.filter((item) => isModelFile(item.file.name));
  if (models.length === 0) {
    const sole = files.length === 1 ? files[0].file.name : '';
    return problem(
      'unsupported',
      sole ? `${sole} is not a supported model.` : 'No supported model in these files.',
      'Choose an FBX or USDZ model, or use a sample below.',
    );
  }
  if (models.length > 1) {
    return problem('multiple-models', 'Choose one model at a time.', `Your pack includes two videos of one model — ${models.length} models were found.`);
  }

  const model = models[0];
  if (model.file.size === 0) {
    return problem('empty', `${model.file.name} is empty.`, 'Export your model again and choose the new file.');
  }

  const totalBytes = files.reduce((sum, item) => sum + item.file.size, 0);
  if (totalBytes > RENDER_UPLOAD_LIMIT_BYTES) {
    return problem(
      'too-large',
      `These files exceed the ${formatBytes(RENDER_UPLOAD_LIMIT_BYTES, 0)} limit.`,
      `${formatBytes(totalBytes)} selected. Reduce the texture sizes or choose a smaller model.`,
    );
  }

  const textures = files.filter((item) => item !== model && isTextureFile(item.file.name));
  // Another model never goes along: its arrival alone can start a render on the backend.
  const others = files.filter((item) => item !== model && !isTextureFile(item.file.name) && !isModelFile(item.file.name));
  const fileName = model.file.name;
  const format = fileExtension(fileName).slice(1);

  return {
    ok: true,
    model: {
      name: fileName.slice(0, fileName.length - format.length - 1),
      fileName,
      format,
      model,
      textures,
      others,
      fileCount: files.length,
      totalBytes,
    },
  };
}

export interface RenderTextureCheck {
  // The model to upload: a texture it asks for that sat elsewhere in the drop now sits beside it.
  model: RenderUploadModel;
  // File names the model asks for that none of the files carries.
  missing: string[];
  // File names the model asks for that are there (moved ones included).
  found: string[];
  // Paths of the textures moved beside the model.
  moved: string[];
}

// Reads the texture list out of an FBX (render-fbx-textures.ts) and holds it against the drop.
// The renderer looks only in the model's own folder, so textures dropped beside it — Sketchfab's
// source/ + textures/ — are moved into it. A model this cannot read passes: the renderer keeps
// the last word, as before.
export async function checkModelTextures(model: RenderUploadModel): Promise<RenderTextureCheck> {
  const scan = model.format === 'fbx' ? await scanFbxTextures(model.model.file) : null;
  if (!scan) {
    return { model, missing: [], found: [], moved: [] };
  }
  const check = checkTextures(scan, model.model.path, uploadFilesOf(model));
  const folder = folderOf(model.model.path);
  const moves = new Map(check.misplaced.map((item) => [item.path, inFolder(folder, item.path)]));
  const move = (item: RenderUploadFile): RenderUploadFile => (moves.has(item.path) ? { ...item, path: moves.get(item.path) as string } : item);
  const fbx = { textures: scan.textures.length, exporter: renderExporterOf(scan.app) };
  return {
    model: moves.size ? { ...model, fbx, textures: model.textures.map(move), others: model.others.map(move) } : { ...model, fbx },
    missing: check.missing.map((texture) => texture.name),
    found: [...check.found, ...check.misplaced.map((item) => item.texture)].map((texture) => texture.name),
    moved: Array.from(moves.keys()),
  };
}

// The model's files with `added` put beside the model (where the renderer looks), replacing a
// file of the same name there. Inspect the result again: the limit may be crossed now.
export function withAddedFiles(model: RenderUploadModel, added: RenderUploadFile[]): RenderUploadFile[] {
  const folder = folderOf(model.model.path);
  const placed = added.map((item) => ({ ...item, path: inFolder(folder, item.path) }));
  const taken = new Set(placed.map((item) => item.path.toLowerCase()));
  return [...uploadFilesOf(model).filter((item) => !taken.has(item.path.toLowerCase())), ...placed];
}

export function uploadFilesOf(model: RenderUploadModel): RenderUploadFile[] {
  return [model.model, ...model.textures, ...model.others];
}

function inFolder(folder: string, path: string): string {
  const name = path.split(/[\\/]/).pop() || path;
  return folder ? `${folder}/${name}` : name;
}

export function formatBytes(bytes: number, digits = 1): string {
  if (bytes < 1024 * 1024) {
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / 1024 / 1024).toFixed(digits)} MB`;
  }
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function problem(code: RenderUploadProblemCode, title: string, text: string): RenderUploadInspection {
  return { ok: false, problem: { code, title, text } };
}
